import { FC, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';

import { EditIcon } from '../../../assets/icons/index';
import Button from '../../../components/button/index';
import { ModalWrapper } from '../../../components/modal-wrapper/index';
import { DividendPayoutType } from '../../../hooks/dividend/dividend.types';
import { useShareClasses } from '../../../hooks/share-class/use-share-classes';
import {
  downloadCompanyStorageFile,
  downloadCompanyStorageFilesSequentially,
} from '../../../utils/company-storage-url.util';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { getTranslation } from '../../../utils/get-translation.util';
import { FilesModal } from '../../events/modals/index';
import { AddDividendFormData } from './add-dividend.validation';
import {
  countImpactedStakeholders,
  countShareClasses,
  getPeriodCovered,
  SummaryRow,
  SummarySection,
} from './dividend-summary-parts';

// Steps each summary block leads into. The flow itself is unchanged — editing just starts on this
// summary instead of at step one (design 4937:31921).
enum EditStep {
  AMOUNTS = 2,
  DETAILS = 1,
  GENERAL_DOCUMENTS = 4,
  STAKEHOLDER_DOCUMENTS = 3,
}

const VISIBLE_FILES = 2;

const EditPencil: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    aria-label={label}
    className="flex size-6 shrink-0 items-center justify-center rounded transition-colors hover:bg-brand-50"
    onClick={onClick}
    title={label}
    type="button"
  >
    <EditIcon className="size-4" iconColor="#2565C8" />
  </button>
);

// The links are storage keys, not addresses a browser may fetch — going straight to them is what
// returned Access Denied (86cbb1wjd). One file downloads, several open the list.
const FileLinks: FC<{ links: string[]; onOpen: () => void }> = ({ links, onOpen }) => {
  if (links.length === 0) return <>—</>;

  const visible = links.slice(0, VISIBLE_FILES);
  const hiddenCount = links.length - visible.length;

  return (
    <button
      // Without a width of its own the button grows to fit the longest name, and the `max-w-full`
      // below then measures against that — the file name runs under the pencil and out of the panel.
      className="flex w-full min-w-0 flex-col items-start gap-1 border-0 bg-transparent p-0 text-left"
      onClick={onOpen}
      type="button"
    >
      {visible.map((link) => (
        <span
          className="block max-w-full truncate text-brand-700 underline"
          key={link}
          title={getS3FileOriginalName(link) || undefined}
        >
          {getS3FileOriginalName(link) || 'document'}
        </span>
      ))}
      {hiddenCount > 0 && <span className="text-brand-700 underline">+{hiddenCount}</span>}
    </button>
  );
};

type Props = {
  companyId: string;
  control: Control<AddDividendFormData>;
  onCancel: () => void;
  onSelectStep: (step: number) => void;
};

export const EditDividendSummary: FC<Props> = ({ companyId, control, onCancel, onSelectStep }) => {
  const [openFiles, setOpenFiles] = useState<{ links: string[]; title: string } | null>(null);
  const formValues = useWatch({ control });
  const { shareClasses } = useShareClasses({ companyId, filter: 'all', take: 200 });

  const {
    filesLinks = [],
    frequency,
    items = [],
    name,
    periodIndex,
    stakeholderDocuments = [],
    type,
    year,
  } = formValues;

  const stakeholderFiles = stakeholderDocuments.flatMap((document) => document.filesLinks ?? []);
  const editLabel = getTranslation('EDIT');

  const showFiles = (links: string[], title: string) => {
    if (links.length === 1) {
      void downloadCompanyStorageFile(companyId, links[0]);
      return;
    }

    setOpenFiles({ links, title });
  };

  return (
    <>
      <ModalWrapper
        className="z-[71] w-fit"
        control={{ onOpenChange: (next) => !next && setOpenFiles(null), open: openFiles !== null }}
        onInteractOutside={() => setOpenFiles(null)}
        overlayClassName="z-[70]"
        side="CENTER"
      >
        <FilesModal
          filesLinks={openFiles?.links ?? []}
          name={openFiles?.title ?? ''}
          onClose={() => setOpenFiles(null)}
          onDownloadFile={(link) => void downloadCompanyStorageFile(companyId, link)}
          onSuccess={() => {
            void downloadCompanyStorageFilesSequentially(companyId, openFiles?.links ?? []);
            setOpenFiles(null);
          }}
        />
      </ModalWrapper>
      <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-6 pt-4">
        <SummarySection
          action={<EditPencil label={editLabel} onClick={() => onSelectStep(EditStep.DETAILS)} />}
          title={getTranslation('GENERAL')}
        >
          <SummaryRow label={getTranslation('PAYOUT_NAME')} value={name} />
          <SummaryRow
            label={getTranslation('PERIOD_COVERED')}
            value={getPeriodCovered({ frequency, periodIndex, year })}
          />
          <SummaryRow
            label={getTranslation('DISTRIBUTION')}
            value={getTranslation(
              type === DividendPayoutType.PER_SHARE_CLASS ? 'PER_SHARE_CLASS' : 'PER_STAKEHOLDER',
            )}
          />
        </SummarySection>

        <SummarySection
          action={<EditPencil label={editLabel} onClick={() => onSelectStep(EditStep.AMOUNTS)} />}
          strong
          title={getTranslation('PAYOUTS')}
        >
          <SummaryRow
            label={getTranslation('STAKEHOLDERS')}
            value={countImpactedStakeholders({ items, shareClasses, type })}
          />
          <SummaryRow label={getTranslation('SHARE_CLASSES')} value={countShareClasses(items)} />
        </SummarySection>

        {/* Per-stakeholder files and the shared ones live on different steps, so each row gets its
            own pencil rather than one for the whole card. */}
        <SummarySection strong title={getTranslation('DOCUMENTS')}>
          <div className="flex items-start justify-between gap-2 pr-4">
            <div className="min-w-0 flex-1">
              <SummaryRow
                hideColon
                label={getTranslation('STAKEHOLDER_DOCUMENTS')}
                value={
                  <FileLinks
                    links={stakeholderFiles}
                    onOpen={() =>
                      showFiles(stakeholderFiles, getTranslation('STAKEHOLDER_DOCUMENTS'))
                    }
                  />
                }
              />
            </div>
            <div className="pt-3">
              <EditPencil
                label={editLabel}
                onClick={() => onSelectStep(EditStep.STAKEHOLDER_DOCUMENTS)}
              />
            </div>
          </div>
          <div className="flex items-start justify-between gap-2 pr-4">
            <div className="min-w-0 flex-1">
              <SummaryRow
                hideColon
                label={getTranslation('GENERAL_DOCUMENTS')}
                value={
                  <FileLinks
                    links={filesLinks}
                    onOpen={() => showFiles(filesLinks, getTranslation('GENERAL_DOCUMENTS'))}
                  />
                }
              />
            </div>
            <div className="pt-3">
              <EditPencil
                label={editLabel}
                onClick={() => onSelectStep(EditStep.GENERAL_DOCUMENTS)}
              />
            </div>
          </div>
        </SummarySection>
      </div>

      <div className="flex w-full shrink-0 items-center px-6">
        <Button
          className="h-9 w-fit rounded border border-gray-100 bg-white px-3 py-1.5 text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
          type="button"
        >
          {getTranslation('CANCEL')}
        </Button>
      </div>
    </>
  );
};
